import { capitalize } from "lodash";
import moment from "moment";
import React, { ReactNode, useEffect } from "react";
import { Link } from "react-router-dom";

import Box from "veneer/Box";
import { JollyRancherChip } from "veneer/Chip";
import DescriptionList, { DescriptionListItem } from "veneer/DescriptionList";
import Divider from "veneer/Divider";
import Drawer from "veneer/Drawer";
import List from "veneer/List";
import ListItem from "veneer/ListItem";
import Skeleton from "veneer/Skeleton";
import Stack from "veneer/Stack";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { getLastConnectedStatusMeta } from "cortex/ui/src/constant/lastConnected";
import {
  LOAD_DEVICE_STATS,
  unloadDeviceStatsAction,
} from "cortex/ui/src/fleet/redux-store/ui-state/device-stats/actions";
import { deviceStatsSelector } from "cortex/ui/src/fleet/redux-store/ui-state/device-stats/selectors";
import { Strategy } from "cortex/ui/src/fleet/view/strategy/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { useFeatureFlag } from "cortex/ui/src/hooks/useFeatureFlag";
import { Network } from "cortex/ui/src/network/model";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { applicationsSelector } from "cortex/ui/src/redux-store/applications/selectors";
import {
  getDeviceNetwork,
  getDeviceType,
  hasConnected,
} from "cortex/ui/src/redux-store/devices/selectors";
import { deviceLastSeenSelector } from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import { isSandbox } from "cortex/ui/src/redux-store/networks";
import { defaultArray } from "cortex/ui/src/redux-store/selectors";
import { Indicator } from "cortex/ui/src/ui/indicator";
import { COLOR_OPTIONS } from "cortex/ui/src/ui/info-box/common";
import { csvStringToArray } from "cortex/ui/src/util/array";
import { Panel } from "cortex/ui/src/view/layout";

import DeviceVersionStatusChip from "./DeviceVersionStatusChip";
import ApplicationViewChart from "../charts/ApplicationViewChart";
import ConnectionChartWrapper from "../charts/ConnectionChartWrapper";
import { Device } from "../model";
import { formatOsBadge } from "../tableFormat";
import { DeviceTags } from "../tags";

interface DeviceDetailsDrawerProps {
  device: Device;
  onClose: () => void;
  open: boolean;
  title: string;
}

interface DeviceSummaryProps {
  device: Device;
  deviceId: string;
  deviceType?: string;
  network: Network;
  osId?: string;
  playerVersion?: string;
  tags: Device["tags"];
  thirdPartySource: string;
}

type DeviceStatsSlice = {
  deviceId?: string;
  stats?: {
    stats?: {
      connection?: ReadonlyArray<readonly [number, boolean]>;
      view?: ReadonlyArray<[number | string, string, string?]>;
    };
    strategy?: StrategyWithStartTime | null;
  };
};

type StrategyWithStartTime = Strategy & {
  startTime?: string | null;
};

const getThirdPartySource = (id?: string) => {
  return id ? capitalize(id.split("/")[0]) : "";
};

function normalizeCsvSource(
  value?: ReadonlyArray<string> | string | null,
): string | null {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value;
  }

  return value.join(",");
}

export default function DeviceDetailsDrawer({
  device,
  onClose,
  open,
  title,
}: DeviceDetailsDrawerProps) {
  const dispatch = useAppDispatch();
  const applications = useAppSelector(applicationsSelector);
  const deviceStats = useAppSelector(
    (state: unknown) => deviceStatsSelector(state) as DeviceStatsSlice,
  );
  const isStrategiesEnabled = useFeatureFlag("is-strategies-enabled");
  const {
    current_version: playerVersion,
    device_id: deviceId,
    ip_addresses: rawIpAddresses,
    mac_addresses: rawMacAddresses,
    os_platform: osId,
    tags,
    thirdparty_id: thirdPartyId,
  } = device;
  const lastSeen = useAppSelector((state: unknown) =>
    deviceLastSeenSelector(state, device),
  );
  const network = useAppSelector((state: unknown) =>
    getDeviceNetwork(state, device),
  );

  useEffect(() => {
    if (!deviceId) {
      return;
    }

    const loadStats = async () => {
      try {
        await createPromiseApiAction(dispatch, LOAD_DEVICE_STATS, [deviceId]);
      } catch (error) {
        console.error("Failed to load device stats:", error);
      }
    };

    void loadStats();

    return () => {
      dispatch(unloadDeviceStatsAction);
    };
  }, [deviceId, dispatch]);

  if (!deviceId || !network) {
    return null;
  }

  const statsLoaded = deviceStats?.deviceId === deviceId;
  const stats = statsLoaded ? deviceStats?.stats?.stats : undefined;
  const strategy = statsLoaded ? (deviceStats?.stats?.strategy ?? null) : null;
  const connectionData = stats?.connection ?? defaultArray;
  const viewData = stats?.view ?? defaultArray;
  const deviceType = getDeviceType(device);
  const isShell = !hasConnected(device);
  const statusMeta = getLastConnectedStatusMeta(lastSeen);
  const ipAddresses = csvStringToArray(
    { sort: true },
    normalizeCsvSource(rawIpAddresses),
  );
  const macAddresses = csvStringToArray(
    { sort: true },
    normalizeCsvSource(rawMacAddresses),
  );
  const thirdPartySource = getThirdPartySource(thirdPartyId);

  return (
    <Drawer onClose={onClose} open={open} padding={0} title={title}>
      <Stack gap={1}>
        <DeviceSummary
          device={device}
          deviceId={deviceId}
          deviceType={deviceType}
          network={network}
          osId={osId}
          playerVersion={playerVersion}
          tags={tags}
          thirdPartySource={thirdPartySource}
        />
        <Stack divider={<Divider />} gap={1} p={1}>
          <DrawerTile title="Connectivity Status">
            <ConnectionStatus statusMeta={statusMeta} />
          </DrawerTile>
          <DrawerTile title="Uptime Last 24h">
            <LoadedContent loaded={statsLoaded}>
              <ConnectionChartWrapper
                connectionData={connectionData}
                isShell={isShell}
              />
            </LoadedContent>
          </DrawerTile>
          {isStrategiesEnabled && (
            <DrawerTile title="Current Strategy">
              <LoadedContent loaded={statsLoaded}>
                <CurrentStrategy strategy={strategy} />
              </LoadedContent>
            </DrawerTile>
          )}
          <DrawerTile title="Display Status">
            <LoadedContent loaded={statsLoaded}>
              <ApplicationViewChart
                applications={applications}
                data={viewData}
                deviceId={deviceId}
              />
            </LoadedContent>
          </DrawerTile>
          <DrawerTile title="IP Addresses">
            <AddressList addresses={ipAddresses} />
          </DrawerTile>
          {macAddresses.length > 0 && (
            <DrawerTile title="MAC Addresses">
              <AddressList addresses={macAddresses} />
            </DrawerTile>
          )}
        </Stack>
      </Stack>
    </Drawer>
  );
}

function DeviceSummary({
  device,
  deviceId,
  deviceType,
  network,
  osId,
  playerVersion,
  tags,
  thirdPartySource,
}: DeviceSummaryProps) {
  const networkId = network?.id ?? 0;
  const networkName = network?.name ?? "";

  return (
    <Box bgcolor={Colors.gray200} p={2}>
      <Panel mb={0} p={0}>
        <DescriptionList>
          <DescriptionListItem label="Device ID">
            <Link to={`/devices/${deviceId}`}>{deviceId}</Link>
          </DescriptionListItem>
          <DescriptionListItem label="Network">
            <Stack alignItems="center" direction="row" spacing={1}>
              <Link
                to={{
                  pathname: "/redirect",
                  search: `to=/networks/${networkId}`,
                }}
              >
                {networkName}
              </Link>
              {isSandbox(network) ? (
                <JollyRancherChip color="gray" label="Sandbox" />
              ) : (
                <JollyRancherChip color="green" label="Production" />
              )}
            </Stack>
          </DescriptionListItem>
          <DescriptionListItem label="Type and Version">
            <Stack alignItems="center" direction="row" gap={0.5}>
              {deviceType && (
                <JollyRancherChip color="orange" label={deviceType} />
              )}
              {playerVersion && <DeviceVersionStatusChip device={device} />}
              {thirdPartySource && (
                <JollyRancherChip color="purple" label={thirdPartySource} />
              )}
            </Stack>
          </DescriptionListItem>
          <DescriptionListItem label="Operating System">
            {osId ? formatOsBadge(osId) : "No info"}
          </DescriptionListItem>
          <DescriptionListItem label="Tags">
            <DeviceTags emptyValue="No tags" hideSystemTags tags={tags ?? []} />
          </DescriptionListItem>
        </DescriptionList>
      </Panel>
    </Box>
  );
}

function LoadedContent({
  children,
  loaded,
}: {
  children: ReactNode;
  loaded: boolean;
}) {
  return loaded ? <>{children}</> : <Skeleton />;
}

const DrawerTile = ({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) => {
  return (
    <Stack gap={2} p={2}>
      <Typography color={Colors.gray1000} variant="h4">
        {title}
      </Typography>
      {children}
    </Stack>
  );
};

function AddressList({ addresses }: { addresses: string[] }) {
  return (
    <Box bgcolor={Colors.blue200} p={2}>
      <Typography color={Colors.gray1000} component="div" variant="body2">
        <List dense disablePadding>
          {addresses.map((address, index) => (
            <ListItem disablePadding key={`${address}-${index}`}>
              {address}
            </ListItem>
          ))}
        </List>
      </Typography>
    </Box>
  );
}

function ConnectionStatus({
  statusMeta,
}: {
  statusMeta: ReturnType<typeof getLastConnectedStatusMeta>;
}) {
  const statusSubtext = statusMeta.tooltipSubtitle
    ? `${statusMeta.tooltipTitle} | ${statusMeta.tooltipSubtitle}`
    : statusMeta.tooltipTitle;
  const color = COLOR_OPTIONS[statusMeta.color] ? statusMeta.color : "gray";
  const { backgroundColor } = COLOR_OPTIONS[color];

  return (
    <Stack alignItems="center" direction="row" gap={2}>
      <Box
        alignItems="center"
        bgcolor={backgroundColor}
        borderRadius="50%"
        display="flex"
        height={52}
        justifyContent="center"
        p={1}
        width={52}
      >
        <Indicator
          color={statusMeta.color}
          size="large"
          type={statusMeta.icon}
        />
      </Box>
      <Stack>
        <Typography color={Colors.gray1000} variant="h3">
          {statusMeta.label}
        </Typography>
        <Typography>{statusSubtext}</Typography>
      </Stack>
    </Stack>
  );
}

function CurrentStrategy({
  strategy,
}: {
  strategy: StrategyWithStartTime | null;
}) {
  if (!strategy?.name) {
    return <Box>Real-time strategy info not available</Box>;
  }

  return (
    <Box>
      {strategy.name}
      {strategy.startTime && (
        <>
          <br />
          The strategy started {moment.utc(strategy.startTime).fromNow()}
        </>
      )}
    </Box>
  );
}
